'use es6';

import { List } from 'immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import ChatHeadingConfigPropType from 'conversations-prop-types/prop-types/ChatHeadingConfigPropType';
import KBArticleHeader from './KBArticleHeader';
import WidgetHeaderContent from '../widget-header/WidgetHeaderContent';
import WidgetHeaderBackButton from '../widget-header/WidgetHeaderBackButton';
import WidgetHeaderStyleWrapper from '../../presentation-components/WidgetHeaderStyleWrapper';
import WidgetViews from '../proptypes/WidgetViews';
import { THREAD_VIEW, THREAD_LIST, KNOWLEDGE_BASE, KNOWLEDGE_BASE_ARTICLE, CATEGORY_VIEW } from '../constants/views';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import SVGClose from 'visitor-ui-component-library-icons/icons/SVGClose';
import styled, { ThemeProvider, withTheme } from 'styled-components';
import { setPrimaryColor, setTextOnPrimaryColor } from 'visitor-ui-component-library/theme/defaultThemeOperators';
import { DEFAULT_TEXT_COLOR } from 'visitor-ui-component-library/theme/ColorConstants';
import { createTheme } from 'visitor-ui-component-library/theme/createTheme';
import { CSSTransition } from 'react-transition-group';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Spacer = styled.div.withConfig({
  displayName: "WidgetHeader__Spacer",
  componentId: "enom9h-0"
})(["flex-grow:1;"]);
class WidgetHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAvailabilityMessage: false
    };
  }
  getAvailabilityMessage() {
    return this.props.preview ? this.props.previewResponseTimeText : this.props.typicalResponseTimeMessage || this.props.officeHoursMessage;
  }
  getShowAvailabilityMessage() {
    const {
      showAvailabilityMessage,
      typicalResponseTimeMessage,
      officeHoursMessage,
      preview,
      previewResponseTimeText
    } = this.props;
    const showRealAvailabilityMessage = showAvailabilityMessage && (typicalResponseTimeMessage || officeHoursMessage);
    if (showRealAvailabilityMessage) {
      return true;
    }
    return Boolean(preview && !!previewResponseTimeText);
  }
  render() {
    const {
      backButtonDisabled,
      browserWindowHeight,
      chatHeadingConfig,
      chatHeadingResponders,
      coloring,
      createNewThread,
      customHeaderText,
      inline,
      isThreadAssigned,
      kbArticleDeepLink,
      mobile,
      navigateToThreadList,
      onClose,
      restorePreviousViewFromKBArticle,
      renderSdkCloseButton,
      showBackButton,
      toggleWidgetSizeForKBArticle,
      unseenThreadsCountExcludingCurrentThread,
      view,
      viewKBArticleExpanded,
      isOnSearchMode,
      handleAnimationEntered,
      handleAnimationExited
    } = this.props;
    const {
      accentColor,
      useDefaultColor
    } = coloring;
    const isKBArticle = view === KNOWLEDGE_BASE_ARTICLE;
    const operators = [setPrimaryColor(accentColor)];
    if (useDefaultColor) {
      operators.push(setTextOnPrimaryColor(DEFAULT_TEXT_COLOR));
    }
    return /*#__PURE__*/_jsx(CSSTransition, {
      in: !isOnSearchMode,
      timeout: {
        enter: 300,
        exit: 300
      },
      classNames: "widget-background-panel",
      unmountOnExit: true,
      onEntered: handleAnimationEntered,
      onExited: handleAnimationExited,
      children: /*#__PURE__*/_jsx(ThemeProvider, {
        theme: createTheme(...operators),
        children: /*#__PURE__*/_jsxs(WidgetHeaderStyleWrapper, {
          mobile: mobile,
          coloring: coloring,
          inline: inline,
          renderSdkCloseButton: renderSdkCloseButton,
          children: [showBackButton && !isKBArticle && /*#__PURE__*/_jsx(WidgetHeaderBackButton, {
            navigateToThreadList: navigateToThreadList,
            unseenThreadsCountExcludingCurrentThread: unseenThreadsCountExcludingCurrentThread,
            disabled: backButtonDisabled
          }), isKBArticle && /*#__PURE__*/_jsx(KBArticleHeader, {
            browserWindowHeight: browserWindowHeight,
            inline: inline,
            kbArticleDeepLink: kbArticleDeepLink,
            restorePreviousViewFromKBArticle: restorePreviousViewFromKBArticle,
            mobile: mobile,
            toggleWidgetSizeForKBArticle: toggleWidgetSizeForKBArticle,
            viewKBArticleExpanded: viewKBArticleExpanded
          }), /*#__PURE__*/_jsx(WidgetHeaderContent, {
            availabilityMessage: this.getAvailabilityMessage(),
            chatHeadingConfig: chatHeadingConfig,
            createNewThread: createNewThread,
            coloring: coloring,
            customHeaderText: customHeaderText,
            showStatusIndicator: isThreadAssigned,
            mobile: mobile,
            chatHeadingResponders: chatHeadingResponders,
            showAvailabilityMessage: this.getShowAvailabilityMessage(),
            showCreateThreadButton: view === THREAD_LIST,
            showKBArticleHeader: view === KNOWLEDGE_BASE_ARTICLE,
            showThreadListHeader: view === THREAD_LIST || view === KNOWLEDGE_BASE || view === CATEGORY_VIEW
          }), /*#__PURE__*/_jsx(Spacer, {}), mobile && !inline && /*#__PURE__*/_jsx(VizExIconButton, {
            "data-test-id": "mobile-close-button",
            onClick: onClose,
            className: "m-left-2",
            use: "transparent-on-primary",
            children: /*#__PURE__*/_jsx(VizExIcon, {
              icon: /*#__PURE__*/_jsx(SVGClose, {})
            })
          })]
        })
      })
    });
  }
}
WidgetHeader.displayName = 'WidgetHeader';
WidgetHeader.defaultProps = {
  createNewThread: () => {},
  inline: false,
  isThreadAssigned: false,
  onClose: () => {},
  navigateToThreadList: () => {},
  showBackButton: false,
  showAvailabilityMessage: false,
  unseenThreadsCountExcludingCurrentThread: 0,
  view: THREAD_VIEW,
  theme: {}
};
WidgetHeader.propTypes = {
  backButtonDisabled: PropTypes.bool,
  browserWindowHeight: PropTypes.number,
  chatHeadingConfig: ChatHeadingConfigPropType.isRequired,
  chatHeadingResponders: PropTypes.instanceOf(List).isRequired,
  coloring: PropTypes.object.isRequired,
  createNewThread: PropTypes.func.isRequired,
  customHeaderText: PropTypes.string,
  handleAnimationEntered: PropTypes.func.isRequired,
  handleAnimationExited: PropTypes.func.isRequired,
  inline: PropTypes.bool.isRequired,
  isOnSearchMode: PropTypes.bool.isRequired,
  isThreadAssigned: PropTypes.bool,
  kbArticleDeepLink: PropTypes.string,
  mobile: PropTypes.bool,
  navigateToThreadList: PropTypes.func,
  officeHoursMessage: PropTypes.string,
  onClose: PropTypes.func,
  preview: PropTypes.bool,
  previewResponseTimeText: PropTypes.string,
  renderSdkCloseButton: PropTypes.func,
  restorePreviousViewFromKBArticle: PropTypes.func,
  showAvailabilityMessage: PropTypes.bool,
  showBackButton: PropTypes.bool,
  theme: PropTypes.object,
  toggleWidgetSizeForKBArticle: PropTypes.func,
  typicalResponseTimeMessage: PropTypes.string,
  unseenThreadsCountExcludingCurrentThread: PropTypes.number,
  view: WidgetViews,
  viewKBArticleExpanded: PropTypes.bool
};

// I hate that I have to do this...but we have nested theme providers
// and I need to pass the theme down to the new theme
// like who does that??
export default withTheme(WidgetHeader);